import { Fragment, type ReactNode } from 'react';
import type { GistAuthor, GistPostView } from '@london-life/contracts';
import { PersonPicture } from '../people/person-card';

// Gist's pages (as Lagos Life's X-style Gist): a post and its thread, a player's page, and the composer open over
// the four tabs; back closes the top one
export type GistPage =
  | { kind: 'post'; id: string; reply?: boolean }
  | { kind: 'user'; id: string; name: string }
  | { kind: 'compose'; append?: GistPostView };
export type GistNav = {
  post: (id: string, options?: { reply?: boolean }) => void;
  user: (lifeId: string, name: string) => void;
  search: (q: string) => void;
  compose: (append?: GistPostView) => void;
  back: () => void;
};

// a player's picture, or London Gist's for a news post
export function GistAvatar({ author, size = 40 }: { author: Pick<GistAuthor, 'name' | 'look'> | null; size?: number }) {
  return (
    <span className="gist-avatar" style={{ width: size, height: size, fontSize: size * 0.48 }} aria-hidden="true">
      {author ? (
        <PersonPicture person={{ firstName: author.name.replace(/^@/, ''), look: author.look }} />
      ) : (
        <span className="gist-news-badge">📰</span>
      )}
    </span>
  );
}

// 1,234 as 1.2K, as Lagos counts
export const compact = (value: number) =>
  value >= 1e6
    ? `${(value / 1e6).toFixed(1).replace(/\.0$/, '')}M`
    : value >= 1e4
      ? `${Math.round(value / 1e3)}K`
      : value >= 1e3
        ? `${(value / 1e3).toFixed(1).replace(/\.0$/, '')}K`
        : String(value);

// words with their #tags and @names as links (a #tag or @name searches Gist)
const LINKS = /([#@][A-Za-z0-9_]{2,30})/g;
export function GistText({ text, onLink }: { text: string; onLink: (q: string) => void }) {
  const parts = text.split(LINKS);
  return (
    <p className="gist-body">
      {parts.map((part, index): ReactNode =>
        index % 2 === 1 ? (
          <button
            key={index}
            type="button"
            className="gist-link"
            onClick={(event) => {
              event.stopPropagation();
              onLink(part);
            }}
          >
            {part}
          </button>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </p>
  );
}

// an empty list: a title and a line under it
export function GistEmpty({ title, body }: { title: string; body: string }) {
  return (
    <div className="gist-empty">
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  );
}
